import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import { applyDocumentLocale } from './documentLocale'
import { SUPPORT_EMAIL } from '../utils/supportEmail'

import heCommon from '@locales/he/common.json'
import heAuth from '@locales/he/auth.json'
import heExpenses from '@locales/he/expenses.json'
import heGroups from '@locales/he/groups.json'
import heShopping from '@locales/he/shopping.json'
import heTasks from '@locales/he/tasks.json'
import heGames from '@locales/he/games.json'
import heSettings from '@locales/he/settings.json'
import heTour from '@locales/he/tour.json'
import heOnboarding from '@locales/he/onboarding.json'
import heCategories from '@locales/he/categories.json'
import heErrors from '@locales/he/errors.json'
import hePush from '@locales/he/push.json'
import heMail from '@locales/he/mail.json'
import heLegal from '@locales/he/legal.json'
import heGuide from '@locales/he/guide.json'
import heBudget from '@locales/he/budget.json'

import enCommon from '@locales/en/common.json'
import enAuth from '@locales/en/auth.json'
import enExpenses from '@locales/en/expenses.json'
import enGroups from '@locales/en/groups.json'
import enShopping from '@locales/en/shopping.json'
import enTasks from '@locales/en/tasks.json'
import enGames from '@locales/en/games.json'
import enSettings from '@locales/en/settings.json'
import enTour from '@locales/en/tour.json'
import enOnboarding from '@locales/en/onboarding.json'
import enCategories from '@locales/en/categories.json'
import enErrors from '@locales/en/errors.json'
import enPush from '@locales/en/push.json'
import enMail from '@locales/en/mail.json'
import enLegal from '@locales/en/legal.json'
import enGuide from '@locales/en/guide.json'
import enBudget from '@locales/en/budget.json'

import arCommon from '@locales/ar/common.json'
import arAuth from '@locales/ar/auth.json'
import arExpenses from '@locales/ar/expenses.json'
import arGroups from '@locales/ar/groups.json'
import arShopping from '@locales/ar/shopping.json'
import arTasks from '@locales/ar/tasks.json'
import arGames from '@locales/ar/games.json'
import arSettings from '@locales/ar/settings.json'
import arTour from '@locales/ar/tour.json'
import arOnboarding from '@locales/ar/onboarding.json'
import arCategories from '@locales/ar/categories.json'
import arErrors from '@locales/ar/errors.json'
import arPush from '@locales/ar/push.json'
import arMail from '@locales/ar/mail.json'
import arLegal from '@locales/ar/legal.json'
import arGuide from '@locales/ar/guide.json'
import arBudget from '@locales/ar/budget.json'

import ruCommon from '@locales/ru/common.json'
import ruAuth from '@locales/ru/auth.json'
import ruExpenses from '@locales/ru/expenses.json'
import ruGroups from '@locales/ru/groups.json'
import ruShopping from '@locales/ru/shopping.json'
import ruTasks from '@locales/ru/tasks.json'
import ruGames from '@locales/ru/games.json'
import ruSettings from '@locales/ru/settings.json'
import ruTour from '@locales/ru/tour.json'
import ruOnboarding from '@locales/ru/onboarding.json'
import ruCategories from '@locales/ru/categories.json'
import ruErrors from '@locales/ru/errors.json'
import ruPush from '@locales/ru/push.json'
import ruMail from '@locales/ru/mail.json'
import ruLegal from '@locales/ru/legal.json'
import ruGuide from '@locales/ru/guide.json'
import ruBudget from '@locales/ru/budget.json'

export const SUPPORTED_LOCALES = ['he', 'en', 'ar', 'ru']
const FALLBACK_LOCALE = 'he'

// i18n.language can carry a region ('en-GB', 'ru-RU') or a language we don't
// ship. Everything that stores or sends a locale goes through here so the four
// call sites can't drift apart — they did once, and an 'ar' user silently got
// Hebrew.
export function resolveLocale(lng) {
  const tag = String(lng || '')
  return SUPPORTED_LOCALES.find((code) => tag.startsWith(code)) || FALLBACK_LOCALE
}

// The Intl locale tag for Date/Number formatting per app language. Was a
// two-way `startsWith('en') ? 'en-US' : 'he-IL'` ternary copy-pasted into six
// screens — every ru/ar user got Hebrew-formatted dates on all of them.
const INTL_LOCALES = { he: 'he-IL', ar: 'ar', en: 'en-US', ru: 'ru-RU' }
export function localeFor(lng) {
  return INTL_LOCALES[resolveLocale(lng)]
}

const NAMESPACES = [
  'common',
  'auth',
  'expenses',
  'groups',
  'shopping',
  'tasks',
  'games',
  'settings',
  'tour',
  'onboarding',
  'categories',
  'errors',
  'push',
  'mail',
  'legal',
  // Frontend-only — the help-center content never needs to render server-side
  // (mail templates, validation errors), so it isn't in backend/services/i18n.js.
  'guide',
  'budget',
]

const resources = {
  he: {
    common: heCommon,
    auth: heAuth,
    expenses: heExpenses,
    groups: heGroups,
    shopping: heShopping,
    tasks: heTasks,
    games: heGames,
    settings: heSettings,
    tour: heTour,
    onboarding: heOnboarding,
    categories: heCategories,
    errors: heErrors,
    push: hePush,
    mail: heMail,
    legal: heLegal,
    guide: heGuide,
    budget: heBudget,
  },
  en: {
    common: enCommon,
    auth: enAuth,
    expenses: enExpenses,
    groups: enGroups,
    shopping: enShopping,
    tasks: enTasks,
    games: enGames,
    settings: enSettings,
    tour: enTour,
    onboarding: enOnboarding,
    categories: enCategories,
    errors: enErrors,
    push: enPush,
    mail: enMail,
    legal: enLegal,
    guide: enGuide,
    budget: enBudget,
  },
  ar: {
    common: arCommon,
    auth: arAuth,
    expenses: arExpenses,
    groups: arGroups,
    shopping: arShopping,
    tasks: arTasks,
    games: arGames,
    settings: arSettings,
    tour: arTour,
    onboarding: arOnboarding,
    categories: arCategories,
    errors: arErrors,
    push: arPush,
    mail: arMail,
    legal: arLegal,
    guide: arGuide,
    budget: arBudget,
  },
  ru: {
    common: ruCommon,
    auth: ruAuth,
    expenses: ruExpenses,
    groups: ruGroups,
    shopping: ruShopping,
    tasks: ruTasks,
    games: ruGames,
    settings: ruSettings,
    tour: ruTour,
    onboarding: ruOnboarding,
    categories: ruCategories,
    errors: ruErrors,
    push: ruPush,
    mail: ruMail,
    legal: ruLegal,
    guide: ruGuide,
    budget: ruBudget,
  },
}

let initPromise

export function initI18n() {
  if (!initPromise) {
    initPromise = i18n
      .use(LanguageDetector)
      .use(initReactI18next)
      .init({
        resources,
        fallbackLng: FALLBACK_LOCALE,
        supportedLngs: SUPPORTED_LOCALES,
        ns: NAMESPACES,
        defaultNS: 'common',
        interpolation: {
          escapeValue: false,
          defaultVariables: { supportEmail: SUPPORT_EMAIL },
        },
        detection: {
          order: ['localStorage', 'navigator'],
          caches: ['localStorage'],
          lookupLocalStorage: 'ms-locale',
        },
      })
      .then(() => {
        applyDocumentLocale(i18n.language, resolveLocale)
        i18n.on('languageChanged', (lng) => applyDocumentLocale(lng, resolveLocale))
        return i18n
      })
  }
  return initPromise
}

export default i18n
